import { useState, useMemo } from "react";
import {
    Modal,
    Box,
    Typography,
    Divider,
    Button,
    Stack,
    Tooltip,
    IconButton,
    TextField,
    Autocomplete
} from "@mui/material";
import { QrCode2, Email, ContentCopy } from "@mui/icons-material";
import QRCode from "react-qr-code";
import styles from "./../css/ProjectInfo.module.css";
import ProjectService from "../../services/ProjectService";
import Chip from "@mui/material/Chip"; // still tiny



export default function ShareInvoiceModalSimple({ open, onClose, invoice, contacts }) {
    const [shareMethod, setShareMethod] = useState(null);
    const [invoiceUrl, setInvoiceUrl] = useState("")
    const [toRecipients, setToRecipients] = useState([]); // array of email strings
    const [emailText, setEmailText] = useState("");

    const buildInvoiceUrl = async () => {
        try {
            const data = await ProjectService.shareInvoice(invoice.id);
            setInvoiceUrl(data.url)
        } catch (error) {
            console.error('Error fetching PDF:', error);
        }
    };
    const selectQrCode = () => {
        buildInvoiceUrl()
        setShareMethod("QR");
    }
    const selectEmail = () => {
        setShareMethod("EMAIL");
        if (!emailText) {
            setEmailText(
                `Hallo ${invoice?.customerName || "Kunde"},\n\n` +
                `anbei finden Sie die Rechnung ${invoice?.number || ""} als PDF-Anhang.\n` +
                `Bitte prüfen Sie die Angaben. Bei Fragen stehen wir Ihnen gerne zur Verfügung.\n\n` +
                `Vielen Dank und freundliche Grüsse\n` +
                `Ihr Team`
            );
        }
    };


    const handleSendEmail = async () => {
        // TODO: send with your backend
        const requestData = {
            to: toRecipients,
            body: emailText
        }
        try {
            const data = await ProjectService.sendEmailInvoice(invoice.id, requestData)
            console.log(data)
        } catch (e) {
            console.log("error sending email")
        }
        onClose?.();
    };

    const clearModal = () => {
        onClose();
        setShareMethod(null);
    }

    const emailRegex =
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/i;

// Turn any input (option object or string) into an email string.
// Supports "Name <email@x.ch>" and plain "email@x.ch".
    function normalizeToEmail(value) {
        if (!value) return "";
        if (typeof value === "string") {
            const m = value.match(/<([^>]+)>/);
            const maybe = m ? m[1] : value;
            return maybe.trim();
        }
        // value is option object
        return value.email || value.name || "";
    }

    function uniqueEmails(arr) {
        const seen = new Set();
        return arr.filter((e) => {
            const k = e.toLowerCase();
            if (seen.has(k)) return false;
            seen.add(k);
            return true;
        });
    }

    return (
        <Modal open={open} onClose={clearModal}>
            <Box className={styles.sheet}>
                <Typography variant="h6">Select share method</Typography>
                <Divider sx={{ my: 2 }} />

                <Stack direction="row" spacing={2} justifyContent="center" sx={{ mb: 2 }}>
                    <Button
                        variant={shareMethod === "QR" ? "contained" : "outlined"}
                        startIcon={<QrCode2 />}
                        onClick={() => {selectQrCode()}}
                    >
                        QR code
                    </Button>
                    <Button
                        variant={shareMethod === "EMAIL" ? "contained" : "outlined"}
                        startIcon={<Email />}
                        onClick={selectEmail}
                    >
                        Send email
                    </Button>
                </Stack>

                {shareMethod === "QR" && (
                    <Stack alignItems="center" spacing={1.5}>
                        <>
                            {invoiceUrl ? (
                                <QRCode value={invoiceUrl} size={200} />
                            ) : (
                                // optional placeholder while loading
                                <div style={{ width: 200, height: 200, display: "grid", placeItems: "center" }}>
                                    Generating link…
                                </div>
                            )}
                        </>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Typography variant="body2" sx={{ wordBreak: "break-all" }}>{invoiceUrl}</Typography>
                            <Tooltip title="Copy link">
                                <IconButton size="small" onClick={async () => { try { await navigator.clipboard.writeText(invoiceUrl); } catch {} }}>
                                    <ContentCopy fontSize="inherit" />
                                </IconButton>
                            </Tooltip>
                        </Stack>
                    </Stack>
                )}

                {shareMethod === "EMAIL" && (
                    <Stack spacing={1.5}>
                        {/* TO: multi-select + free typing */}
                        <Autocomplete
                            multiple
                            freeSolo
                            // give it your real contacts array
                            options={(contacts || []).map(c => ({
                                label: c.name ? `${c.name} <${c.email}>` : c.email,
                                email: c.email,
                            }))}
                            // value is an array of email strings (state you keep in parent)
                            value={toRecipients /* e.g. useState([]) */}
                            onChange={(_, newValue) => {
                                // newValue can be strings or option objects; normalize to emails
                                const emails = uniqueEmails(
                                    newValue
                                        .map(normalizeToEmail)
                                        .filter(Boolean)
                                );
                                setToRecipients(emails);
                            }}
                            // how options render in the input
                            getOptionLabel={(opt) => (typeof opt === "string" ? opt : opt.label || opt.email || "")}
                            renderTags={(value, getTagProps) =>
                                value.map((email, index) => (
                                    <Chip
                                        {...getTagProps({ index })}
                                        key={email}
                                        label={email}
                                        color={emailRegex.test(email) ? "default" : "error"}
                                        variant="outlined"
                                        size="small"
                                    />
                                ))
                            }
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="To"
                                    placeholder="Type email and press Enter…"
                                    error={toRecipients.some((e) => !emailRegex.test(e))}
                                    helperText={
                                        toRecipients.some((e) => !emailRegex.test(e))
                                            ? "One or more addresses look invalid."
                                            : ""
                                    }
                                />
                            )}
                            // keep UX nice
                            selectOnFocus
                            clearOnBlur
                            handleHomeEndKeys
                            filterSelectedOptions
                            fullWidth
                        />

                        {/* (Optional) CC field: duplicate the Autocomplete above with ccRecipients / setCcRecipients */}

                        <TextField
                            label="Message"
                            multiline
                            minRows={6}
                            value={emailText}
                            onChange={(e) => setEmailText(e.target.value)}
                            fullWidth
                        />

                        <Stack direction="row" justifyContent="flex-end" spacing={1}>
                            <Button onClick={onClose}>Cancel</Button>
                            <Button
                                variant="contained"
                                onClick={handleSendEmail}
                                disabled={toRecipients.length === 0 || toRecipients.some((e) => !emailRegex.test(e))}
                            >
                                Send
                            </Button>
                        </Stack>
                    </Stack>
                )}


                {!shareMethod && (
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center" }}>
                        Choose a method above to continue.
                    </Typography>
                )}
            </Box>
        </Modal>
    );
}
